import React, { useCallback, useEffect, useRef } from 'react';
import { Drawer } from 'vaul';
import { X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { getEffectiveSnapPoint, getEffectiveSnapPoints } from '@/lib/sheet-utils';
import { useReducedMotion } from '@/hooks/useReducedMotion';
import { useSurfaceManager } from '@/context/SurfaceContext';
import { useIsSheetMobile } from '@/components/ui/use-mobile';
import { useScrollFieldIntoView } from '@/hooks/useScrollFieldIntoView';

export type SheetSnapPoint = 'peek' | 'half' | 'full';

const SNAP_VALUES: Record<SheetSnapPoint, number> = {
  peek: 0.25,
  half: 0.5,
  full: 0.9,
};

interface BottomSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Initial snap point when the sheet opens */
  snapPoint?: SheetSnapPoint;
  /** Which snap points are available (defaults to all three) */
  snapPoints?: SheetSnapPoint[];
  /** Unique ID for stacking management */
  id?: string;
  /** Content */
  children: React.ReactNode;
  /** Accessible title (required for a11y) */
  title: string;
  /** Optional description */
  description?: string;
  /** Additional class on the content panel */
  className?: string;
  /** If true, the sheet cannot be dismissed by swiping/clicking overlay */
  dismissible?: boolean;
  /** Override drag behavior. Defaults to false */
  handleOnly?: boolean;
  /** Content rendered above the scroll area that allows swipe-to-dismiss (no data-vaul-no-drag) */
  dragZone?: React.ReactNode;
}

export const BottomSheet: React.FC<BottomSheetProps> = ({
  open,
  onOpenChange,
  snapPoint = 'half',
  snapPoints: allowedSnapPoints,
  id,
  children,
  title,
  description,
  className,
  dismissible = true,
  handleOnly,
  dragZone,
}) => {
  const reducedMotion = useReducedMotion();
  const surfaceManager = useSurfaceManager();
  const isSheetMobile = useIsSheetMobile();
  const scrollRef = useRef<HTMLDivElement>(null);
  useScrollFieldIntoView(scrollRef);

  // Phase 0: Remap snap points on mobile (<1024px) — peek → half, dedup
  const effectiveSnap = getEffectiveSnapPoint(snapPoint, isSheetMobile);
  const effectiveAllowed = getEffectiveSnapPoints(
    allowedSnapPoints ?? ['peek', 'half', 'full'],
    isSheetMobile,
  );
  const snaps = effectiveAllowed.map(s => SNAP_VALUES[s]);
  const initialSnap = SNAP_VALUES[effectiveSnap];

  // Phase 2: Full sheets get strict drag handling by default
  const isFull = effectiveSnap === 'full';

  // Compute explicit height for the snap point so flex children can scroll
  const snapHeight = `${initialSnap * 100}dvh`;

  // One-level stacking: when this sheet opens, close any other open sheet
  useEffect(() => {
    if (open && id) {
      surfaceManager.registerSheet(id);
      return () => surfaceManager.unregisterSheet(id);
    }
  }, [open, id, surfaceManager]);

  const handleOpenChange = useCallback((nextOpen: boolean) => {
    onOpenChange(nextOpen);
  }, [onOpenChange]);

  // Swipe-down on overlay (the inactive scrim above the sheet) dismisses
  const overlayTouchY = useRef(0);
  const handleOverlayTouchStart = useCallback((e: React.TouchEvent) => {
    overlayTouchY.current = e.touches[0].clientY;
  }, []);
  const handleOverlayTouchEnd = useCallback((e: React.TouchEvent) => {
    const delta = e.changedTouches[0].clientY - overlayTouchY.current;
    if (delta > 30 && dismissible) onOpenChange(false);
  }, [dismissible, onOpenChange]);

  return (
    <Drawer.Root
      open={open}
      onOpenChange={handleOpenChange}
      snapPoints={snaps}
      activeSnapPoint={open ? initialSnap : undefined}
      fadeFromIndex={snaps.length - 1}
      dismissible={dismissible}
      handleOnly={handleOnly ?? false}
      scrollLockTimeout={100}
      modal
    >
      <Drawer.Portal>
        <Drawer.Overlay
          className="fixed inset-0 z-[60] bg-black/40 transition-opacity"
          style={reducedMotion ? { transition: 'none' } : undefined}
          onTouchStart={handleOverlayTouchStart}
          onTouchEnd={handleOverlayTouchEnd}
        />
        <Drawer.Content
          className={cn(
            "fixed inset-x-0 bottom-0 z-[61] flex flex-col",
            "bg-[var(--rc-surface-primary)] rounded-t-[var(--rc-radius-lg)]",
            "border-t border-[var(--rc-border-subtle)]",
            "shadow-[var(--rc-shadow-elevated)]",
            "outline-none",
            "max-h-[92dvh]",
            className,
          )}
          style={{
            height: snapHeight,
            ...(reducedMotion ? { transition: 'none' } : undefined),
          }}
        >
          {/* Drag handle */}
          <Drawer.Handle className="mx-auto mt-3 mb-2 w-10 h-1 rounded-full bg-[var(--rc-ink-ghost)]" />

          {/* X close button for full sheets */}
          {isFull && dismissible && (
            <button
              onClick={() => onOpenChange(false)}
              className="absolute top-3 right-3 z-10 p-1.5 rounded-[var(--rc-radius-sm)] bg-[var(--rc-accent-acid)] text-[var(--rc-ink-primary)] hover:bg-[var(--rc-accent-acid-hover)] transition-colors"
              aria-label="Close"
            >
              <X size={18} />
            </button>
          )}

          {/* Accessible title */}
          <Drawer.Title className="sr-only">{title}</Drawer.Title>
          {description && <Drawer.Description className="sr-only">{description}</Drawer.Description>}

          {/* Drag zone — swipeable area above scroll content (no data-vaul-no-drag) */}
          {dragZone}

          {/* Scrollable content — data-vaul-no-drag prevents content scroll from dismissing */}
          <div
            ref={scrollRef}
            data-vaul-no-drag
            className="flex-1 min-h-0 overflow-y-auto overscroll-contain px-6 pb-[env(safe-area-inset-bottom)]"
          >
            {children}
          </div>
        </Drawer.Content>
      </Drawer.Portal>
    </Drawer.Root>
  );
};
